import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import ScrollToTop from './components/ScrollToTop';
import LandingPage from './pages/LandingPage';
import ServicesPage from './pages/ServicesPage';
import PropertiesPage from './pages/PropertiesPage';
import PropertyDetailPage from './pages/PropertyDetailPage';
import GiftsPage from './pages/GiftsPage';
import BlogPage from './pages/BlogPage';
import ItineraryBuilderPage from './pages/ItineraryBuilderPage';
import VendorAPIPage from './pages/VendorAPIPage';
import VendorRegisterPage from './pages/VendorRegisterPage';
import ClientDashboard from './pages/ClientDashboard';
import VendorDashboard from './pages/VendorDashboard';
import AdminDashboard from './pages/AdminDashboard';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import BulkRFPFormPage from './pages/BulkRFPFormPage';
import ClientRFPDashboard from './pages/ClientRFPDashboard';
import HotelInventoryPage from './pages/HotelInventoryPage';
import VendorOpenRFPs from './pages/VendorOpenRFPs';
import ElementSheetPage from './pages/ElementSheetPage';
import CareersPage from './pages/CareersPage';
// Negotiation & Bidding System Pages
import CreateRFPPage from './pages/CreateRFPPage';
import ClientRFPManagementPage from './pages/ClientRFPManagementPage';
import BidComparisonPage from './pages/BidComparisonPage';
import NegotiationChatPage from './pages/NegotiationChatPage';
import VendorBiddingPage from './pages/VendorBiddingPage';
// Business Protection System Pages
import AdminViolationsPage from './pages/AdminViolationsPage';
// Event Permissions System Page
import EventPermissionsPage from './pages/EventPermissionsPage';
// Policy Pages
import RefundPolicyPage from './pages/RefundPolicyPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsConditionsPage from './pages/TermsConditionsPage';
import PricingPage from './pages/PricingPage';
import TrainersPage from './pages/TrainersPage';
// Itinerary Planner
import ItineraryPlannerPage from './pages/ItineraryPlannerPage';
// Invitation Builder
import InvitationBuilderPage from './pages/InvitationBuilderPage';
import { AuthProvider, useAuth } from './context/AuthContext';
import '@/App.css';

function ProtectedRoute({ children, allowedRoles }) {
  const { user } = useAuth();
  
  if (!user) {
    return <Navigate to="/" />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" />;
  }
  
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <div className="App noise-texture">
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/properties" element={<PropertiesPage />} />
            <Route path="/properties/:id" element={<PropertyDetailPage />} />
            <Route path="/gifts" element={<GiftsPage />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/vendor-register" element={<VendorRegisterPage />} />
            <Route path="/hotel-registration" element={<VendorRegisterPage />} />
            <Route path="/partner-with-us" element={<VendorRegisterPage />} />
            <Route path="/refund-policy" element={<RefundPolicyPage />} />
            <Route path="/cancellation-policy" element={<RefundPolicyPage />} />
            <Route path="/payment-policy" element={<RefundPolicyPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms-conditions" element={<TermsConditionsPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/trainers" element={<TrainersPage />} />
            <Route path="/plans" element={<PricingPage />} />
            <Route path="/subscription" element={<PricingPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/itinerary-builder" element={<ItineraryBuilderPage />} />
            <Route path="/itinerary-planner/:id" element={
              <ProtectedRoute allowedRoles={['client', 'admin']}>
                <ItineraryPlannerPage />
              </ProtectedRoute>
            } />
            <Route path="/invitation-builder" element={
              <ProtectedRoute allowedRoles={['client', 'admin']}>
                <InvitationBuilderPage />
              </ProtectedRoute>
            } />
            <Route
              path="/vendor/api"
              element={
                <ProtectedRoute allowedRoles={['vendor']}>
                  <VendorAPIPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/client"
              element={
                <ProtectedRoute allowedRoles={['client']}>
                  <ClientDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/vendor"
              element={
                <ProtectedRoute allowedRoles={['vendor']}>
                  <VendorDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/admin"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/payment-cancel" element={<PaymentCancel />} />
            {/* Bulk RFP Routes */}
            <Route
              path="/bulk-rfp/new"
              element={
                <ProtectedRoute allowedRoles={['client']}>
                  <BulkRFPFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/client/rfps"
              element={
                <ProtectedRoute allowedRoles={['client']}>
                  <ClientRFPDashboard />
                </ProtectedRoute>
              }
            />
            {/* Hotel Inventory Routes */}
            <Route
              path="/vendor/inventory"
              element={
                <ProtectedRoute allowedRoles={['vendor']}>
                  <HotelInventoryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vendor/open-rfps"
              element={
                <ProtectedRoute allowedRoles={['vendor']}>
                  <VendorOpenRFPs />
                </ProtectedRoute>
              }
            />
            {/* Element Sheet */}
            <Route
              path="/element-sheet"
              element={
                <ProtectedRoute allowedRoles={['client', 'admin']}>
                  <ElementSheetPage />
                </ProtectedRoute>
              }
            />
            {/* Negotiation & Bidding System Routes */}
            <Route
              path="/client/rfps/create"
              element={
                <ProtectedRoute allowedRoles={['client']}>
                  <CreateRFPPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/rfps"
              element={
                <ProtectedRoute allowedRoles={['client']}>
                  <ClientRFPManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/rfps/:rfpId/bids"
              element={
                <ProtectedRoute allowedRoles={['client']}>
                  <BidComparisonPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/bids/:bidId/negotiate"
              element={
                <ProtectedRoute allowedRoles={['client']}>
                  <NegotiationChatPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vendor/rfps"
              element={
                <ProtectedRoute allowedRoles={['vendor']}>
                  <VendorBiddingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vendor/bids/:bidId/negotiate"
              element={
                <ProtectedRoute allowedRoles={['vendor']}>
                  <NegotiationChatPage />
                </ProtectedRoute>
              }
            />
            {/* Business Protection System Routes */}
            <Route
              path="/admin/violations"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminViolationsPage />
                </ProtectedRoute>
              }
            />
            {/* Event Permissions System Routes */}
            <Route
              path="/event-permissions"
              element={<EventPermissionsPage />}
            />
            <Route
              path="/client/permissions"
              element={
                <ProtectedRoute allowedRoles={['client']}>
                  <EventPermissionsPage />
                </ProtectedRoute>
              }
            />
          </Routes>
          <Toaster position="top-right" />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
